// src/components/tables/columnBuilders.tsx
/**
 * Column builder utilities for creating common column configurations.
 * These help reduce boilerplate when defining table columns.
 */

import type { SortableColumn } from "./SortableTable";

// ============================================================================
// Types
// ============================================================================

export interface ColumnBuilderOptions {
  /** Column ID */
  id: string;
  /** Column header text */
  header: string;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Whether column is sortable */
  sortable?: boolean;
  /** Custom CSS class for header */
  className?: string;
  /** Minimum column width */
  minWidth?: number;
  /** Fixed column width */
  width?: number;
}

// Font weight mappings
const fontWeights = {
  normal: '',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as const;

// ============================================================================
// Column Builders
// ============================================================================

/**
 * Create a currency column
 */
export function currencyColumn<RowT>(
  options: ColumnBuilderOptions & {
    accessor: (row: RowT) => number | null | undefined;
    symbol?: string;
    compact?: boolean;
    decimals?: number;
  }
): SortableColumn<RowT> {
  const { accessor, symbol = '$', compact = true, decimals = 2, align = 'right', ...rest } = options;

  const formatCompact = (value: number): string => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(decimals)}B`;
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(decimals)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(decimals)}K`;
    return value.toFixed(decimals);
  };

  return {
    ...rest,
    align,
    cell: (row: RowT) => {
      const value = accessor(row);
      if (value == null || !Number.isFinite(value)) {
        return <span className="text-slate-400">–</span>;
      }
      
      const formatted = compact ? formatCompact(value) : value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals,
      });
      
      return <span className="tabular-nums text-slate-200">{symbol}{formatted}</span>;
    },
    sortValue: (row: RowT) => accessor(row) ?? -Infinity,
  };
}

/**
 * Create a percentage column with color coding
 */
export function percentColumn<RowT>(
  options: ColumnBuilderOptions & {
    accessor: (row: RowT) => number | null | undefined;
    decimals?: number;
    showSign?: boolean;
    colorize?: boolean;
    alreadyPercent?: boolean;
    fontWeight?: 'normal' | 'medium' | 'semibold';
  }
): SortableColumn<RowT> {
  const { 
    accessor, 
    decimals = 2, 
    showSign = true, 
    colorize = true,
    alreadyPercent = false,
    fontWeight = 'semibold',
    align = 'right',
    ...rest 
  } = options;

  const fontClass = fontWeights[fontWeight];

  return {
    ...rest,
    align,
    cell: (row: RowT) => {
      const value = accessor(row);
      if (value == null || !Number.isFinite(value)) {
        return <span className="text-slate-400">–</span>;
      }
      
      const displayValue = alreadyPercent ? value : value * 100;
      const sign = showSign && displayValue >= 0 ? '+' : '';
      const colorClass = colorize 
        ? (displayValue >= 0 ? 'text-emerald-400' : 'text-rose-400')
        : 'text-slate-200';
      
      return (
        <span className={`tabular-nums ${fontClass} ${colorClass}`}>
          {sign}{displayValue.toFixed(decimals)}%
        </span>
      );
    },
    sortValue: (row: RowT) => accessor(row) ?? -Infinity,
  };
}

/**
 * Create a ratio column (e.g., P/E, MC/TVL)
 */
export function ratioColumn<RowT>(
  options: ColumnBuilderOptions & {
    accessor: (row: RowT) => number | null | undefined;
    decimals?: number;
    suffix?: string;
  }
): SortableColumn<RowT> {
  const { accessor, decimals = 2, suffix = '', align = 'right', ...rest } = options;

  return {
    ...rest,
    align,
    cell: (row: RowT) => {
      const value = accessor(row);
      if (value == null || !Number.isFinite(value)) {
        return <span className="text-slate-400">–</span>;
      }
      
      return (
        <span className="tabular-nums">
          {value.toFixed(decimals)}{suffix}
        </span>
      );
    },
    sortValue: (row: RowT) => accessor(row) ?? -Infinity,
  };
}

// ============================================================================
// Specialized Column Builders
// ============================================================================

/**
 * Create an asset column with logo, name, and ticker
 */
export function assetColumn<RowT>(
  options: ColumnBuilderOptions & {
    tickerAccessor: (row: RowT) => string;
    nameAccessor: (row: RowT) => string;
    logoAccessor: (row: RowT) => string | undefined;
    fallbackLogo?: string;
    size?: 'sm' | 'md' | 'lg';
    showTicker?: boolean;
  }
): SortableColumn<RowT> {
  const { 
    tickerAccessor, 
    nameAccessor, 
    logoAccessor, 
    fallbackLogo = '/hermes_terminal_cubism.png',
    size = 'md',
    showTicker = true,
    sortable = false,
    ...rest 
  } = options;

  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-8 h-8',
    lg: 'w-10 h-10',
  };

  return {
    ...rest,
    align: 'left',
    sortable,
    cell: (row: RowT) => {
      const ticker = tickerAccessor(row);
      const name = nameAccessor(row);
      const logo = logoAccessor(row);
      
      return (
        <div className="flex items-center gap-2.5">
          <img
            src={logo || fallbackLogo}
            alt={ticker}
            className={`${sizeClasses[size]} rounded-full flex-shrink-0`}
            onError={(e) => {
              (e.target as HTMLImageElement).src = fallbackLogo;
            }}
          />
          <div className="flex flex-col min-w-0">
            <span className="text-slate-100 font-medium text-sm truncate">{name}</span>
            {showTicker && <span className="text-slate-400 text-xs">{ticker}</span>}
          </div>
        </div>
      );
    },
    sortValue: (row: RowT) => nameAccessor(row),
  };
}

